import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import {
  useAddPlanzoneVirtualResource,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { Resource, resource } from "@planzone/dto";
import { ScreenWatcher } from "@common/screenWatcher";
import { LayoutWatcher } from "@common/layoutWatcher";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { Props } from "./types";
import { VirtualResourceForm } from "./virtualResourceForm";
import styles from "./styles.scss";

function PlanzoneVirtualResourceAddition({ cancel }: Props) {
  const { t } = useTranslation();
  const { isRequestPending, addPlanzoneVirtualResource } =
    useAddPlanzoneVirtualResource();
  const { planzone } = useRetrieveCurrentPlanzone();
  const [formData, setFormData] = React.useState<Resource[]>([resource]);

  return (
    <Modal header={t("planzoneVirtualResourceAddition.title")} close={cancel}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          formData.forEach((resource) =>
            addPlanzoneVirtualResource({ planzone, resource }),
          );
        }}
        className={styles.planzoneVirtualResourceAddition}
      >
        <div className={styles.main}>
          <div className={styles.row}>
            <div className={styles.group}>
              <h3 className={styles.label}>
                {t("virtualResourceForm.nameTitle")}
              </h3>
            </div>
            <div className={styles.group}>
              <h3 className={styles.label}>
                {t("virtualResourceForm.roleTitle")}
              </h3>
            </div>
            <div className={styles.group}>
              <h3 className={styles.label}>
                {t("virtualResourceForm.professionTitle")}
              </h3>
            </div>
          </div>
          <ScreenWatcher
            renderChildren={({ height }) => (
              <LayoutWatcher
                renderChildren={({ top }) => {
                  const maxHeight = height - top - 60;
                  const [reachedMaxHeight, setReachedMaxHeight] =
                    useState(false);
                  const elementRef = useRef<HTMLDivElement>(null);

                  const hasReachedMaxHeight = () => {
                    const element = elementRef.current;
                    if (element) {
                      const hasReachedMaxHeight =
                        element.scrollHeight >= maxHeight - 60;
                      setReachedMaxHeight(hasReachedMaxHeight);
                    }
                  };

                  return (
                    <div
                      ref={elementRef}
                      className={classNames(styles.forms, {
                        [styles.formsOverflow]: reachedMaxHeight,
                      })}
                      style={{ maxHeight }}
                    >
                      {formData.map((data, index) => (
                        <div
                          key={index}
                          style={{ zIndex: formData.length - index }}
                        >
                          <VirtualResourceForm
                            formData={data}
                            edit={(nextData) =>
                              setFormData([
                                ...formData.slice(0, index),
                                nextData,
                                ...formData.slice(index + 1),
                              ])
                            }
                          />
                        </div>
                      ))}
                      <p
                        className={styles.addResourceButton}
                        onClick={() => {
                          hasReachedMaxHeight();
                          setFormData([...formData, resource]);
                        }}
                      >
                        + {t("planzoneVirtualResourceAddition.addResource")}
                      </p>
                    </div>
                  );
                }}
              />
            )}
          />
          <div className={styles.buttons}>
            <Button type="cancel" press={cancel}>
              {t("planzoneVirtualResourceAddition.cancel")}
            </Button>
            <Button
              type="submit"
              status={isRequestPending ? "pending" : "none"}
            >
              {t("planzoneVirtualResourceAddition.confirm")}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export { PlanzoneVirtualResourceAddition };
